<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Select from 'primevue/select';
import { useRbacStore } from '@/stores/rbac.js';
import { useConfirm } from 'primevue/useconfirm';
import { useServerTable } from '@/composables/useServerTable.js';
import employeeApi from '@/api/employeeApi.js';
import ButtonGroup from "primevue/buttongroup";

const emit = defineEmits(['edit', 'delete', 'configure-permissions']);

const rbacStore = useRbacStore();
const confirm = useConfirm();

const selectedDepartment = ref(null);

const {
  data: positions,
  totalRecords,
  loading,
  rowsPerPage,
  currentPage,
  onPageChange,
  firstIndex,
  setExtra,
  load
} = useServerTable({
  initialRows: 10,
  debounceMs: 0,
  loadPage: async ({ page, ...extra }) => {
    const params = { page };
    if (extra.department) {
      params.department = extra.department;
    }
    return await employeeApi.getPositions(params);
  }
});

// Опции отделов для фильтра
const departmentOptions = computed(() => {
  return [
    { label: 'Все отделы', value: null },
    ...rbacStore.departments.map(dept => ({
      label: dept.name,
      value: dept.id
    }))
  ];
});

onMounted(async () => {
  // Загружаем отделы
  if (rbacStore.departments.length === 0) {
    await rbacStore.loadDepartments();
  }
  // Загружаем должности через useServerTable
  await load();
});

async function handleDepartmentFilter(deptId) {
  selectedDepartment.value = deptId;
  setExtra({ department: deptId });
}

function filterByDepartment(deptId) {
  selectedDepartment.value = deptId;
  setExtra({ department: deptId });
}

defineExpose({
  filterByDepartment
});

function handleEdit(position) {
  emit('edit', position);
}

function handleDelete(position) {
  confirm.require({
    message: `Удалить должность "${position.name}"?`,
    header: 'Подтверждение',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    accept: () => {
      emit('delete', position);
    }
  });
}

function handleConfigurePermissions(position) {
  emit('configure-permissions', position);
}
</script>

<template>
  <div class="h-full flex flex-col">
    <DataTable
      :value="positions"
      :loading="loading"
      stripedRows
      lazy
      scrollable
      scroll-height="flex"
      paginator
      :rows="rowsPerPage"
      :totalRecords="totalRecords"
      :first="firstIndex()"
      @page="onPageChange"
      class="flex-1 text-sm"
    >
      <Column field="name" header="Название должности" style="min-width: 250px"></Column>
      <Column field="department_name" header="Отдел" style="min-width: 200px">
        <template #body="{ data }">
          {{ data.department_name || '—' }}
        </template>
      </Column>
      <Column field="check_ip" header="Проверка IP" style="min-width: 120px; text-align: center;">
        <template #body="{ data }">
          <i v-if="data.check_ip" class="pi pi-check text-green-600"></i>
          <i v-else class="pi pi-times text-gray-400"></i>
        </template>
      </Column>
      <Column header="Действия" style="min-width: 280px">
        <template #body="{ data }">
          <div class="flex gap-2">
            <ButtonGroup>
              <Button
              icon="pi pi-key"
              severity="secondary"
              size="small"
              text
              rounded
              @click="handleConfigurePermissions(data)"
            />
            <Button
              icon="pi pi-pencil"
              severity="secondary"
              size="small"
              rounded
              text
              @click="handleEdit(data)"
            />
            <Button
              icon="pi pi-trash"
              severity="warn"
              size="small"
              rounded
              text
              @click="handleDelete(data)"
            />
            </ButtonGroup>

          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
</style>
